import {
  DigitalBudgetNestedTableRowData,
  TableHeader,
} from 'pages/calculator/MediaPlanning/common/BudgetTable/interfaces';
import { formatNumberWithSpaces } from 'pages/calculator/MediaPlanning/common/BudgetTable/utils/formatNumberWithSpaces';
import {
  TableCell,
  TableRow,
  Typography,
  Table,
  TableHead,
  TableBody,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ErrorTooltip from 'pages/calculator/MediaPlanning/common/BudgetTable/DigitalBudgetTable/NestedBudgetTable/ErrorTooltip';

interface NestedBudgetTableProps {
  rowData?: DigitalBudgetNestedTableRowData[];
  isTotalBudgetRow?: boolean;
}

function NestedBudgetTable({
  rowData,
  isTotalBudgetRow,
}: NestedBudgetTableProps) {
  const { t } = useTranslation();

  const nestedTableHeaders: TableHeader[] = [
    {
      key: isTotalBudgetRow ? 'mediaPlanning.type' : 'mediaPlanning.phase',
      align: 'left',
      width: '250px',
    },
    { key: isTotalBudgetRow ? '' : 'mediaPlanning.priority', align: 'left' },
    { key: 'mediaPlanning.impressions', align: 'right' },
    { key: 'mediaPlanning.reach', align: 'right' },
    { key: 'mediaPlanning.clicks', align: 'right' },
    { key: 'mediaPlanning.videoViews', align: 'right' },
    { key: 'mediaPlanning.budget', align: 'right' },
  ];

  // TODO: it's mock, remove it when use zustand
  const checkIsRFPhaseError = (touchpointFunnel?: string) => {
    if (!touchpointFunnel) {
      return false;
    }

    // toggle to true to show error
    return touchpointFunnel.includes('RF') && false;
  };

  return (
    <Table size="small" sx={{ tableLayout: 'fixed' }}>
      <TableHead>
        <TableRow>
          {nestedTableHeaders.map((header) => (
            <TableCell
              key={header.key}
              align={header.align}
              sx={{ width: header.width || 'auto' }}
            >
              <Typography
                variant="caption"
                textTransform="uppercase"
                fontWeight={600}
              >
                {t(header.key)}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rowData?.map((nestedRow, index) => (
          <TableRow
            key={nestedRow.touchpointFunnel + String(index)}
            sx={{
              '& .MuiTypography-root': {
                color: checkIsRFPhaseError(nestedRow.touchpointFunnel)
                  ? 'error.main'
                  : 'inherit',
              },
            }}
          >
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {checkIsRFPhaseError(nestedRow.touchpointFunnel) && (
                  <ErrorTooltip title={t('mediaPlanning.rfPhaseError')} />
                )}
                <Typography variant="body2">
                  {!isTotalBudgetRow
                    ? nestedRow.touchpointFunnel
                    : nestedRow.type}
                </Typography>
              </Box>
            </TableCell>
            <TableCell>
              {!isTotalBudgetRow && (
                <Typography variant="body2">
                  {nestedRow.isMandatory
                    ? t('mediaPlanning.mustHave')
                    : t('mediaPlanning.optional')}
                </Typography>
              )}
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2">
                {formatNumberWithSpaces(nestedRow.impressions)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2">
                {formatNumberWithSpaces(nestedRow.reach)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2">
                {formatNumberWithSpaces(nestedRow.clicks)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2">
                {formatNumberWithSpaces(nestedRow.videoViews)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2">
                {`${formatNumberWithSpaces(
                  nestedRow.budget.value?.toFixed(0)
                )} ${nestedRow.budget.currency}`}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default NestedBudgetTable;
